import { v4 } from 'uuid';

/**
 * Recursively flat map the items
 * @param {Array} items the items
 * @param {Array} arr the array
 * @param {boolean} preserveChildren if the children should be preserved
 * @returns {Array} the flat array
 */
export const recursiveFlatMap = (items, arr = [], preserveChildren = false) => {
  items.forEach((item) => {
    const { children, ...rest } = item;
    if (children?.length > 0) {
      recursiveFlatMap(children, arr, preserveChildren);
    }
    if (preserveChildren) {
      arr.push({ ...rest, children });
    } else {
      arr.push(rest);
    }
  });
  return arr;
};

/**
 * Recursively set the order of the options
 * @param {object[]} options the list of options
 * @returns {object[]} the options with the order set
 */
export const recursiveSetOptionOrder = (options) =>
  options.map((option, index) => {
    const retVal = option;
    retVal.order = index + 1;
    if (retVal.children) {
      retVal.children = recursiveSetOptionOrder(retVal.children);
    }
    return retVal;
  });

/**
 * Recursively duplicate the template options and set a new ID
 * @param {object} options the options
 * @param {string} parentId the parent ID
 * @returns {Array} the duplicated options
 */
export const recursiveDuplicateTemplateOptions = (
  options,
  parentId = undefined,
) =>
  options.map((option) => {
    const id = v4();
    const { children, ...rest } = option;
    return {
      ...rest,
      id,
      parent_id: parentId,
      children:
        children.length > 0
          ? recursiveDuplicateTemplateOptions(children, id)
          : [],
    };
  });

/**
 * Create a flat array of options
 * @param {Array} templateOptions the options from the form
 * @param {Array} options the options from the previous state
 * @returns {{add: *[]}|{add: *[], remove: *[], update: *[]}}
 */
const createFlatOptions = (templateOptions, options = []) => {
  const addOnly = options.length === 0;
  const orderOptions = recursiveSetOptionOrder(templateOptions);
  const flatOptions = recursiveFlatMap(orderOptions, []);
  if (addOnly) {
    return { add: flatOptions };
  }
  // Here we need to compare the templateOptions with the options to see what was added / removed / updated
  const flatPrevOptions = recursiveFlatMap(options, []);

  // Find the options that were added
  const add = flatOptions.filter(
    (option) =>
      !flatPrevOptions.find((prevOption) => prevOption.id === option.id),
  );

  // Find the options that were removed
  const removedOptions = flatPrevOptions
    .filter(
      (prevOption) =>
        !flatOptions.find((option) => prevOption.id === option.id),
    )
    .map((option) => option.id);

  // We need to make sure that all the children are also removed.

  const removedChildren =
    removedOptions?.length > 0
      ? flatPrevOptions
          ?.filter(
            (prevOption) =>
              removedOptions.find(
                (option) => option === prevOption.parent_id,
              ) && !removedOptions.find((option) => option === prevOption.id),
          )
          ?.map((option) => option.id)
      : [];

  const remove = [...removedChildren, ...removedOptions];

  // Find the options that were updated
  const toUpdate = flatOptions.filter((option) => {
    const prevOption = flatPrevOptions.find((prev) => prev.id === option.id);
    return prevOption && JSON.stringify(prevOption) !== JSON.stringify(option);
  });

  const update = toUpdate.map((option) => {
    const prevOption = flatPrevOptions.find((prev) => prev.id === option.id);
    const dependencies = {
      add: [],
      remove: [],
    };
    dependencies.add = option.dependencies.filter((dep) => {
      const x = !prevOption.dependencies.find((prevDep) => prevDep === dep);
      return x;
    });
    dependencies.remove = prevOption.dependencies.filter(
      (prevDep) => !option.dependencies.find((dep) => prevDep === dep),
    );
    return { ...option, dependencies };
  });

  return { add, remove, update };
};

export default createFlatOptions;
